import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clients as clientsApi, plans as plansApi, campaigns } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { CalendarIcon, ClockIcon, UserIcon, PackageIcon, CheckIcon } from '../common/icons.jsx';

const VEHICLE_CAPACITY = 42;

export default function PlanCreate() {
  const navigate = useNavigate();
  const { activeCampaign } = useAuth();
  const [clientsList, setClientsList] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [driverId, setDriverId] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [breakTime, setBreakTime] = useState('12:30');
  const [breakDuration, setBreakDuration] = useState(45);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      clientsApi.list(),
      campaigns.listUsers(activeCampaign),
    ])
      .then(([cls, users]) => {
        setClientsList(cls.filter((c) => c.delivery_status !== 'delivered'));
        setDrivers(users.filter((u) => u.role === 'driver' || u.role === 'coordinator'));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [activeCampaign]);

  const toggleClient = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalUnits = clientsList
    .filter((c) => selectedIds.has(c.id))
    .reduce((sum, c) => sum + (c.nb_units || 0), 0);

  const fillPercent = Math.min(Math.round((totalUnits / VEHICLE_CAPACITY) * 100), 100);
  const overCapacity = totalUnits > VEHICLE_CAPACITY;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.size === 0 || !driverId) return;
    setSubmitting(true);
    setError('');
    try {
      const plan = await plansApi.create({
        date, driverId, startTime,
        clientIds: [...selectedIds],
        vehicleCapacity: VEHICLE_CAPACITY,
        breakTime: breakDuration > 0 ? breakTime : null,
        breakDuration: breakDuration > 0 ? breakDuration : 0,
      });
      navigate(`/plans/${plan.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-[#1e40af] border-t-transparent rounded-full" />
      </div>
    );
  }

  const canSubmit = selectedIds.size > 0 && driverId && !submitting;

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-[#0f172a]">Nouvelle tournée</h2>
      {error && <p className="text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Date / Time section */}
        <SectionCard title="Date et heure" icon={<CalendarIcon size={18} className="text-[#1e40af]" />}>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Date">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#1e40af] focus:border-transparent outline-none transition-all" required />
            </FormField>
            <FormField label="Heure départ">
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#1e40af] focus:border-transparent outline-none transition-all" />
            </FormField>
            <FormField label="Pause déjeuner">
              <input type="time" value={breakTime} onChange={(e) => setBreakTime(e.target.value)}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#1e40af] focus:border-transparent outline-none transition-all" />
            </FormField>
            <FormField label="Durée pause (min)">
              <input type="number" value={breakDuration} onChange={(e) => setBreakDuration(Number(e.target.value))}
                min="0" max="120" step="15"
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#1e40af] focus:border-transparent outline-none transition-all" />
            </FormField>
          </div>
        </SectionCard>

        {/* Driver section */}
        <SectionCard title="Livreur" icon={<UserIcon size={18} className="text-[#f97316]" />}>
          <select value={driverId} onChange={(e) => setDriverId(e.target.value)}
            className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#1e40af] focus:border-transparent outline-none transition-all" required>
            <option value="">-- Choisir un livreur --</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>{d.name} ({d.role})</option>
            ))}
          </select>
        </SectionCard>

        {/* Capacity gauge */}
        <CapacityGauge total={totalUnits} max={VEHICLE_CAPACITY} percent={fillPercent} over={overCapacity} count={selectedIds.size} />

        {/* Client selection */}
        <SectionCard title="Clients" icon={<PackageIcon size={18} className="text-[#0891b2]" />}>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {clientsList.map((c) => (
              <ClientCheckbox key={c.id} client={c} checked={selectedIds.has(c.id)} onToggle={() => toggleClient(c.id)} />
            ))}
            {clientsList.length === 0 && (
              <p className="text-sm text-[#64748b] text-center py-4">Aucun client à livrer.</p>
            )}
          </div>
        </SectionCard>

        {/* Submit button */}
        <button type="submit" disabled={!canSubmit}
          className={`w-full rounded-xl p-4 text-center font-bold text-base min-h-[56px] transition-all ${
            canSubmit
              ? 'bg-gradient-to-r from-[#1e40af] to-blue-500 text-white shadow-lg hover:shadow-xl active:scale-[0.98]'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}>
          {submitting ? 'Création en cours...' : 'Créer la tournée'}
        </button>
      </form>
    </div>
  );
}

/**
 * @param {{ title: string, icon: JSX.Element, children: React.ReactNode }} props
 */
function SectionCard({ title, icon, children }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm font-semibold text-[#0f172a]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

/** @param {{ label: string, children: React.ReactNode }} props */
function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#64748b] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

/**
 * Visual capacity gauge bar.
 * @param {{ total: number, max: number, percent: number, over: boolean, count: number }} props
 */
function CapacityGauge({ total, max, percent, over, count }) {
  const barColor = over
    ? 'bg-gradient-to-r from-[#dc2626] to-red-400'
    : percent > 75
      ? 'bg-gradient-to-r from-[#f97316] to-amber-400'
      : 'bg-gradient-to-r from-[#059669] to-emerald-400';

  return (
    <div className={`rounded-2xl p-4 shadow-sm ${over ? 'bg-red-50 border border-red-200' : 'bg-white'}`}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-semibold text-[#0f172a]">
          {count} client{count !== 1 ? 's' : ''} - {total} unités
        </span>
        <span className={`text-sm font-bold ${over ? 'text-[#dc2626]' : 'text-[#059669]'}`}>
          {total}/{max}
        </span>
      </div>
      <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
      {over && (
        <p className="text-xs text-[#dc2626] font-medium mt-1.5">Capacite depassee !</p>
      )}
    </div>
  );
}

/**
 * Client card with checkbox styling.
 * @param {{ client: object, checked: boolean, onToggle: () => void }} props
 */
function ClientCheckbox({ client, checked, onToggle }) {
  return (
    <label className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border min-h-[44px] ${
      checked
        ? 'bg-blue-50 border-[#1e40af] ring-1 ring-[#1e40af]/20'
        : 'bg-white border-gray-200 hover:border-gray-300'
    }`}>
      <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${
        checked ? 'bg-[#1e40af] border-[#1e40af]' : 'border-gray-300'
      }`}>
        {checked && <CheckIcon size={12} className="text-white" />}
      </div>
      <input type="checkbox" checked={checked} onChange={onToggle} className="sr-only" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-[#0f172a] truncate">{client.name}</div>
        <div className="text-xs text-[#64748b] truncate">{client.city || client.address}</div>
      </div>
      <span className="text-xs font-semibold text-[#64748b] flex-shrink-0 bg-gray-100 px-2 py-1 rounded-lg">
        {client.nb_units || 0} u.
      </span>
    </label>
  );
}

/** @returns {string} */
function todayStr() {
  return new Date().toISOString().split('T')[0];
}
